/* eslint-disable jsx-a11y/anchor-is-valid */
import {useEffect, useMemo, useState} from 'react'
import {createPortal} from 'react-dom'
import {Modal} from 'react-bootstrap'
import {useQuery} from 'react-query'
import {KTSVG} from '../../../../../../_metronic/helpers'
import {useToast} from '../../../../components/Toats'
import {LoadingSpan} from '../../../../components/Loadings/loadingSpan'
import {getTagsAll, linkTagPlant, unlinkTagPlant} from '../../../../../services/tagsService'
import {Plants} from '../../../../../@types/plants'
import {ModalAddUpdateTag} from '../../../../tagsUsinas/components/ModalAddEditTag'
import styles from './modalLinkPlantTags.module.css'

type TagItem = {
  id: string
  name: string
  color?: string | null
  status?: string
  description?: string | null
}

type Props = {
  plant: Plants | null
  show: boolean
  handleClose: () => void
}

const modalsRoot = document.getElementById('root-modals') || document.body

const ModalLinkPlantTags = ({show, handleClose, plant}: Props) => {
  const {toggleToast} = useToast()
  const [search, setSearch] = useState('')
  const [linkedIds, setLinkedIds] = useState<string[]>([])
  const [initialLinkedIds, setInitialLinkedIds] = useState<string[]>([])
  const [saving, setSaving] = useState(false)
  const [showCreateTagModal, setShowCreateTagModal] = useState(false)

  const {data: tags = [], isLoading, refetch} = useQuery<TagItem[]>(
    ['getTagsAllModalLink', show, plant?.id],
    async () => {
      const response = await getTagsAll()
      return Array.isArray(response) ? response : response?.data || []
    },
    {
      enabled: show,
      onError: () => {
        toggleToast('Erro ao buscar as tags. ', 'error')
      },
    }
  )

  useEffect(() => {
    if (show) {
      const currentIds = (plant?.tags || []).map((tag) => tag.id)
      setSearch('')
      setLinkedIds(currentIds)
      setInitialLinkedIds(currentIds)
      setSaving(false)
    }
  }, [show, plant])

  const filteredTags = useMemo(() => {
    const term = search
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim()

    return (tags || []).filter((tag) => {
      if (tag.status && tag.status !== 'ATIVO') return false
      if (!term) return true
      const name = String(tag.name || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
      return name.includes(term)
    })
  }, [tags, search])

  const linkedTags = filteredTags.filter((tag) => linkedIds.includes(tag.id))
  const availableTags = filteredTags.filter((tag) => !linkedIds.includes(tag.id))
  const hasChanges =
    linkedIds.length !== initialLinkedIds.length ||
    linkedIds.some((id) => !initialLinkedIds.includes(id))

  const handleToggleTag = (tag: TagItem) => {
    if (saving) return

    setLinkedIds((prev) =>
      prev.includes(tag.id) ? prev.filter((id) => id !== tag.id) : [...prev, tag.id]
    )
  }

  const closed = () => {
    setSearch('')
    setSaving(false)
    handleClose()
  }

  const handleSave = async () => {
    if (!plant?.id || saving) return

    const toLink = linkedIds.filter((id) => !initialLinkedIds.includes(id))
    const toUnlink = initialLinkedIds.filter((id) => !linkedIds.includes(id))

    if (toLink.length === 0 && toUnlink.length === 0) {
      closed()
      return
    }

    try {
      setSaving(true)
      await Promise.all([
        ...toLink.map((tagId) => linkTagPlant(tagId, plant.id)),
        ...toUnlink.map((tagId) => unlinkTagPlant(tagId, plant.id)),
      ])
      toggleToast('Tags atualizadas com sucesso. ', 'success')
      closed()
    } catch (error) {
      console.log(error)
      toggleToast('Erro ao salvar as tags. ', 'error')
      setSaving(false)
    }
  }

  const renderTag = (tag: TagItem, linked: boolean) => (
    <button
      key={tag.id}
      type='button'
      className={`${styles.tagBadge} ${linked ? styles.tagBadgeLinked : styles.tagBadgeAvailable}`}
      title={linked ? 'Clique para desvincular' : 'Clique para vincular'}
      style={{backgroundColor: tag.color || '#3498DB'}}
      disabled={saving}
      onClick={() => handleToggleTag(tag)}
    >
      {tag.name}
    </button>
  )

  return createPortal(
    <>
    <Modal
      id='kt_modal_link_plant_tags'
      tabIndex={-1}
      aria-hidden='true'
      dialogClassName='modal-dialog modal-dialog-centered mw-650px'
      show={show}
      onHide={closed}
      backdrop={true}
    >
      <div className='modal-header'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>
            Tags da usina {plant?.name ? `- ${plant.name}` : ''}
          </span>
        </h3>

        <div className='btn btn-sm btn-icon btn-active-color-primary' onClick={closed}>
          <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
        </div>
      </div>

      <div className='modal-body py-lg-8 px-lg-10'>
        <div className='notice d-flex bg-light-info rounded border-info border border-dashed p-4 mb-5'>
          <i className='bi bi-info-circle fs-2 text-info me-3'></i>
          <div className='fs-7 text-info fw-semibold'>
            Clique nas tags disponíveis para vincular e nas já vinculadas para desvincular.
            As alterações só são aplicadas ao salvar.
          </div>
        </div>

        <div className={styles.searchRow}>
          <div className={styles.searchWrap}>
            <span className={styles.searchIcon}>
              <KTSVG path='/media/icons/duotune/general/gen021.svg' className='svg-icon-2' />
            </span>
            <input
              type='text'
              className={styles.searchInput}
              placeholder='Buscar tag pelo nome'
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>

          <button
            type='button'
            className='btn btn-sm btn-light-primary flex-shrink-0'
            disabled={saving}
            onClick={() => setShowCreateTagModal(true)}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-3' />
            Nova Tag
          </button>
        </div>

        {isLoading ? (
          <div className='d-flex justify-content-center py-10'>
            <LoadingSpan />
          </div>
        ) : (
          <>
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <KTSVG path='/media/icons/duotune/general/gen037.svg' className='svg-icon-3 svg-icon-success' />
                <span className='text-dark fw-bold fs-7'>Vinculadas</span>
                <span className='badge badge-light-success'>{linkedTags.length}</span>
              </div>
              <div className={styles.tagsWrap}>
                {linkedTags.length > 0 ? (
                  linkedTags.map((tag) => renderTag(tag, true))
                ) : (
                  <span className={styles.emptyText}>Nenhuma tag vinculada.</span>
                )}
              </div>
            </div>

            <div className='separator my-4'></div>

            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-3 svg-icon-primary' />
                <span className='text-dark fw-bold fs-7'>Disponíveis</span>
                <span className='badge badge-light'>{availableTags.length}</span>
              </div>
              <div className={styles.tagsWrap}>
                {availableTags.length > 0 ? (
                  availableTags.map((tag) => renderTag(tag, false))
                ) : (
                  <span className={styles.emptyText}>Nenhuma tag disponível.</span>
                )}
              </div>
            </div>
          </>
        )}

        <div className={styles.footer}>
          <button
            type='button'
            className='btn btn-lg btn-light'
            disabled={saving}
            onClick={closed}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr063.svg' className='svg-icon-3 me-2' />
            Cancelar
          </button>

          <button
            type='button'
            className='btn btn-lg btn-primary'
            disabled={saving || isLoading || !hasChanges}
            onClick={handleSave}
          >
            Salvar
            {saving ? (
              <LoadingSpan style={{marginLeft: '0.5rem'}} />
            ) : (
              <KTSVG path='/media/icons/duotune/arrows/arr064.svg' className='svg-icon-3 ms-2 me-0' />
            )}
          </button>
        </div>
      </div>
    </Modal>

      <ModalAddUpdateTag
        show={showCreateTagModal}
        data={plant?.id ? {idsPlants: [plant.id]} as any : undefined}
        handleClose={async () => {
          const previousIds = new Set((tags || []).map((tag) => tag.id))
          setShowCreateTagModal(false)
          const result = await refetch()
          const nextTags = result.data || []
          const createdTag = nextTags.find((tag) => !previousIds.has(tag.id))
          if (createdTag) {
            setLinkedIds((prev) => prev.includes(createdTag.id) ? prev : [...prev, createdTag.id])
            setInitialLinkedIds((prev) => prev.includes(createdTag.id) ? prev : [...prev, createdTag.id])
          }
        }}
      />
    </>,
    modalsRoot
  )
}

export {ModalLinkPlantTags}
